import { z } from 'zod';

// ---------------------------------------------------------------------------
// Two-phase, human-confirmed destruction of a PRODUCTION base.
//
// WayAI owns initiate, confirm,
// cancel, the email and the grace timer; the data side's entire new surface is one
// attested mark-for-erasure operation.
//
// The gate that matters is the EMAILED confirmation, not the channel. An agent
// operating the console or the CLI holds the human's own credentials, so no in-band
// check can prove a human decided — only a link that has to be opened in a mail
// client can. Surface omission (no CLI verb, no MCP tool) reduces accidents; the
// email is the guarantee.
// ---------------------------------------------------------------------------

/**
 * WayAI-side mount for the destruction control plane, relative to the `/api` router.
 *
 * Deliberately NOT under `DATA_PROXY_MOUNT`: everything below that prefix is forwarded
 * verbatim to the data Worker's `/v1` tree by any caller holding `org:write`, which is
 * exactly the authority this flow exists to sit above. A separate mount keeps initiate,
 * confirm and cancel on routes WayAI validates itself.
 */
export const BASE_DESTRUCTION_MOUNT = '/bases';

/** Absolute prefix the destruction routes answer on. Rate-limit config keys off this. */
export const BASE_DESTRUCTION_PREFIX = `/api${BASE_DESTRUCTION_MOUNT}`;

/**
 * The emailed link's path. Public (mounted before `authMiddleware`, in `AUTH_BYPASS_PATHS`)
 * because it is opened from a cold mail client with no session.
 *
 * **This route CONFIRMS NOTHING.** It verifies the token's signature and hands the reader to
 * the console; the transition happens on {@link baseDestructionConfirmPath}, a POST. The split
 * is not ceremony — a GET that mutates is completed by anything that dereferences a URL, and
 * enterprise mail-security scanners, link rewriters and gateway prefetchers all do exactly
 * that. Confirming on GET would have armed a production erasure seconds after the mail landed,
 * with no human involved, which is the one thing this whole flow exists to prevent. The
 * `GET /api/users/deletion/undo` precedent is safe on GET only because it STOPS a deletion;
 * dereferencing this one would start one.
 *
 * Declared here rather than spelled at each site: the mount, the bypass entry, the rate-limit
 * key and the URL the email embeds must move together, and a drift between any two of them
 * either 401s every confirmation link or leaves an unauthenticated path un-rate-limited.
 */
export const BASE_DESTRUCTION_CONFIRM_PATH = `${BASE_DESTRUCTION_PREFIX}/destruction/confirm`;

/**
 * The authenticated POST that actually confirms, relative to {@link BASE_DESTRUCTION_MOUNT}.
 *
 * Org-admin gated like initiate and cancel, so the authority is re-checked AT the step the
 * design calls the guarantee. Without that, a token stayed usable for its whole 7-day window
 * by someone whose grant had since been revoked — and their user id was what got attested to
 * the data side as the principal authorizing an irreversible erasure.
 *
 * The guarantee is unweakened by requiring a session: completing the flow still needs the
 * token, which only reaches a mailbox. An agent holding the admin's session and no mailbox
 * access cannot confirm; a human with the mailbox and no admin grant cannot either.
 */
export function baseDestructionConfirmPath(baseId: string): string {
  return `/${encodeURIComponent(baseId)}/destruction/confirm`;
}

/** Query param carrying the emailed token into the console, for the POST to consume. */
export const BASE_DESTRUCTION_TOKEN_QUERY_PARAM = 'destruction_token';

export const baseDestructionConfirmBody = z.object({
  token: z.string().min(1).max(4096),
});
export type BaseDestructionConfirmBody = z.infer<typeof baseDestructionConfirmBody>;

/**
 * Grace window between confirmation and the mark (knob 1, owner-accepted 24 hours).
 *
 * Cancelable by any org admin from the console or the API for its whole length — cancel
 * is safe, so it carries no second factor (knob 2 rules out any undo AFTER it: three
 * independent chances to stop is the design, and an undo past physical erasure would
 * need a shadow copy).
 */
export const BASE_DESTRUCTION_GRACE_MS = 24 * 60 * 60 * 1000;

/**
 * How long an unconfirmed request stays confirmable.
 *
 * Without it a token minted once would confirm a year later, since the confirmation
 * handler's freshness comes entirely from the live request row. Checked against the
 * server-written `requested_at`, so there is no clock-skew term — unlike the invite
 * token, whose issued-at rides in the token itself.
 */
export const BASE_DESTRUCTION_CONFIRM_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Query param naming the org, mirroring the data proxy's selector for multi-org users. */
export const BASE_DESTRUCTION_ORG_QUERY_PARAM = 'org_id';

export const baseDestructionQuery = z.object({
  [BASE_DESTRUCTION_ORG_QUERY_PARAM]: z.string().uuid().optional(),
});
export type BaseDestructionQuery = z.infer<typeof baseDestructionQuery>;

export const baseDestructionParam = z.object({
  base_id: z.string().min(1).max(128),
});
export type BaseDestructionParam = z.infer<typeof baseDestructionParam>;

/**
 * Type-the-name confirm, validated server-side.
 *
 * The id, not the display name: it is what the base's existing Danger Zone already asks
 * for, it is immutable (a `name` can be edited between reading the warning and typing),
 * and it is what every error message about this base quotes.
 */
export const baseDestructionInitiateBody = z.object({
  confirm_base_id: z.string().min(1).max(128),
});
export type BaseDestructionInitiateBody = z.infer<typeof baseDestructionInitiateBody>;

/**
 * `awaiting_confirmation` — initiated, links mailed, nothing scheduled. NOTHING is marked from
 * this state, which is the whole point of the two phases.
 * `confirmed` — an org admin opened a link and confirmed; `execute_after` is set and the grace
 * timer is armed. Still cancelable.
 * `marking` — the grace window elapsed and the mark is IN FLIGHT. No longer cancelable, and
 * that is load-bearing rather than cosmetic: the timer awaits a cross-Worker call and a fetch
 * is not a storage operation, so the Durable Object's input gate stays open across it. Without
 * a claimed state a cancel landing in that window deleted the row and reported success while
 * the erasure was already issued — telling an admin their data was saved as it was destroyed.
 */
export const baseDestructionStatus = z.enum(['awaiting_confirmation', 'confirmed', 'marking']);
export type BaseDestructionStatus = z.infer<typeof baseDestructionStatus>;

export const baseDestructionRequest = z.object({
  /**
   * Identifies THIS initiation, and is what the confirmation token is bound to.
   *
   * A UUID rather than the request's `requested_at`, which is what an earlier draft used and
   * what a test caught: Workers freezes the clock between IO, so two initiates for the same
   * base can carry a byte-identical timestamp — and a link minted for the first would then
   * confirm the second, a destruction its recipient never read the warning for. A value that
   * is unique by construction cannot have that window at any clock granularity.
   */
  request_id: z.string(),
  base_id: z.string(),
  base_name: z.string().nullable(),
  status: baseDestructionStatus,
  requested_at: z.string(),
  requested_by_user_id: z.string(),
  requested_by_email: z.string().nullable(),
  /** Null until a human opens the link. Its presence IS the record that one did. */
  confirmed_at: z.string().nullable(),
  confirmed_by_user_id: z.string().nullable(),
  /** ISO instant the mark fires, or null while awaiting confirmation. */
  execute_after: z.string().nullable(),
  /** ISO instant the confirmation link stops working. */
  confirm_expires_at: z.string(),
  /** Failed mark attempts. Non-zero means the timer is retrying under backoff. */
  attempts: z.number(),
  last_error: z.string().nullable(),
});
export type BaseDestructionRequest = z.infer<typeof baseDestructionRequest>;

export const baseDestructionListResponse = z.object({
  destructions: z.array(baseDestructionRequest),
});
export type BaseDestructionListResponse = z.infer<typeof baseDestructionListResponse>;

export const baseDestructionInitiateResponse = z.object({
  destruction: baseDestructionRequest,
  /** How many org admins the confirmation link went to. Zero is a refusal, not a state. */
  notified_admins: z.number(),
});
export type BaseDestructionInitiateResponse = z.infer<typeof baseDestructionInitiateResponse>;

export const baseDestructionConfirmResponse = z.object({
  destruction: baseDestructionRequest,
});
export type BaseDestructionConfirmResponse = z.infer<typeof baseDestructionConfirmResponse>;

export const baseDestructionCancelResponse = z.object({
  /** False when there was nothing pending — the cancel button is idempotent by design. */
  cancelled: z.boolean(),
  base_id: z.string(),
});
export type BaseDestructionCancelResponse = z.infer<typeof baseDestructionCancelResponse>;

/**
 * Outcomes the public confirmation link redirects on. The handler never renders a body:
 * it is opened in a mail client, so it lands the human in the console with a message,
 * exactly as the account-deletion undo link does.
 */
export const BASE_DESTRUCTION_CONFIRM_MESSAGES = {
  /** The token verified. Nothing is confirmed yet — the console asks for the click. */
  review: 'base_destruction_review',
  confirmed: 'base_destruction_confirmed',
  invalid: 'base_destruction_invalid',
  expired: 'base_destruction_expired',
  unavailable: 'base_destruction_unavailable',
} as const;
export type BaseDestructionConfirmMessage =
  (typeof BASE_DESTRUCTION_CONFIRM_MESSAGES)[keyof typeof BASE_DESTRUCTION_CONFIRM_MESSAGES];

/**
 * Where a confirmation link lands the human: the base's own settings page.
 *
 * Not `/login`, which is where the account-deletion undo link goes — that link's recipient is
 * mid-deletion and has no session, while this one is an org admin who almost certainly does. The
 * destination matters because the page IS the feedback: it renders "Deletion confirmed — will be
 * erased on {at}" with a cancel button, so a confirming admin sees the consequence of the click
 * they just made, and a stale link's owner sees the truth (nothing pending) rather than a message
 * asserting it.
 *
 * Built here rather than in the web app because the backend is what redirects, and the two must
 * name the same route. The `msg` rides along for a client that wants to toast it; the page is
 * correct without one.
 */
export function baseDestructionConsolePath(
  orgId: string,
  baseId: string,
  msg: BaseDestructionConfirmMessage,
  /** The emailed token, carried through so the console's POST can consume it. */
  token?: string,
): string {
  const suffix = token ? `&${BASE_DESTRUCTION_TOKEN_QUERY_PARAM}=${encodeURIComponent(token)}` : '';
  return `/settings/organizations/${encodeURIComponent(orgId)}/bases/${encodeURIComponent(baseId)}`
    + `?msg=${msg}${suffix}`;
}
